import { Actions, FormGridContainer, handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  SetVerificationMethodMutation,
  SetVerificationMethodMutationVariables,
  useSetVerificationMethodMutation,
} from "@graphql/generated";
import { useOrganizationStore } from "@store/useOrganizationStore";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { VerificationState } from ".";
import { inputConfig } from "./enterOrgEmailAndPhoneFormConfig";

interface IEnterOrgEmailAndPhoneStep {
  handleNextStep: (data: VerificationState) => void;
  initialData: VerificationState;
}

const EnterOrgEmailAndPhoneStep = ({ handleNextStep, initialData }: IEnterOrgEmailAndPhoneStep) => {
  const formHandler = useForm<VerificationState>({
    defaultValues: initialData,
  });
  const { selectedOrganization } = useOrganizationStore();

  const [setVerificationMethod, { loading }] = useSetVerificationMethodMutation();

  useEffect(() => {
    if (initialData.email && initialData.phone) {
      formHandler.reset(initialData);
    }
  }, [initialData, formHandler]);

  const handleSendVerifyCode = useCallback(async () => {
    const values = formHandler.getValues();

    if (!values.email || !values.phone) {
      toast.error("Please enter both email and phone number");
      return;
    }

    if (!selectedOrganization?.id) {
      toast.error("No organization selected");
      return;
    }

    await handleApiRequest<SetVerificationMethodMutation, SetVerificationMethodMutationVariables>({
      apiFunction: setVerificationMethod,
      variables: {
        id: selectedOrganization.id,
        communicateorganizationmethod: {
          email: values.email,
          phonenumber: values.phone,
        },
      },
      componentName: "EnterOrgEmailAndPhoneStep",
      actionName: "setVerificationMethod",
      onSuccess: () => {
        handleNextStep(values);
      },
      successMessage: "Verification code sent successfully",
      fallbackErrorMessage: "Failed to send verification code",
      extraErrorContext: {
        organizationId: selectedOrganization.id,
        email: values.email,
      },
    });
  }, [formHandler, handleNextStep, selectedOrganization?.id, setVerificationMethod]);

  return (
    <div className="flex flex-col justify-center items-center mt-3">
      <p className="text-sm text-zinc-500">
        Please enter your contact details. We will send a verification code to your mobile number.
      </p>
      <div className="md:w-9/12 mx-auto mt-6">
        <FormGridContainer formHandler={formHandler as any} inputConfigs={inputConfig} />

        <Actions
          className="mt-6 !grid-cols-1"
          showCancel={false}
          submitProps={{
            onClick: formHandler.handleSubmit(handleSendVerifyCode),
            className: "w-full md:w-fit px-12 block mx-auto",
            isLoading: loading,
            isDisabled: loading,
            children: "Send Verification Code",
          }}
        />
      </div>
    </div>
  );
};

export default EnterOrgEmailAndPhoneStep;
